import axios from "axios";
import Cookies from "js-cookie";
import jwt_decode from "jwt-decode";
import router from "@/router";
import { captureException } from "@/plugins/sentry";

const ACCESS_COOKIE = "g_at";
// Cookie del refresh token de versiones anteriores: ya no se guarda (el front
// nunca refresca la sesion), solo se borra si quedo en el navegador.
const LEGACY_REFRESH_COOKIE = "g_rt";

// La API no lee cookies (el token viaja en Authorization): la cookie es solo
// almacen del navegador. Secure evita que salga por http (en local se sirve por
// http y se omite), strict evita enviarla en navegacion desde otros sitios, y
// expires la hace caducar junto con el token.
const accessCookieAttributes = (access_token) => {
  const attributes = {
    path: "/",
    sameSite: "strict",
    secure: window.location.protocol === "https:",
  };
  try {
    const { exp } = jwt_decode(access_token);
    if (exp) {
      attributes.expires = new Date(exp * 1000);
    }
  } catch (e) {
    // Token ilegible: queda como cookie de sesion
  }
  return attributes;
};

const getToken = () => {
  const access_token = Cookies.get(ACCESS_COOKIE);

  return { access_token };
};

// Recibe la sesion completa ({ access_token, refresh_token }) pero solo guarda
// el access token.
const setToken = ({ access_token }) => {
  Cookies.set(ACCESS_COOKIE, access_token, accessCookieAttributes(access_token));
  Cookies.remove(LEGACY_REFRESH_COOKIE, { path: "/" });
  axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
};

const clearToken = () => {
  Cookies.remove(ACCESS_COOKIE, { path: "/" });
  Cookies.remove(LEGACY_REFRESH_COOKIE, { path: "/" });
  axios.defaults.headers.common["Authorization"] = null;
};

const responseInterceptor = (error) => {
  const status = error.response?.status;

  if (status === 401) {
    clearToken();
    router.push({ name: "Login" });

    return Promise.reject(error);
  }

  // Peticiones canceladas (el usuario navego a otra vista): no son fallos
  if (axios.isCancel(error)) {
    return Promise.reject(error);
  }

  // Solo se reportan errores del servidor o de red; los 4xx restantes son
  // validaciones esperadas que maneja cada vista.
  if (!status || status >= 500) {
    const method = (error.config?.method || "GET").toUpperCase();
    const url = error.config?.url || "desconocida";

    // Se reporta con un mensaje propio para que Sentry agrupe por endpoint
    // en lugar de juntar todo bajo un unico "Network Error".
    const reported = new Error(
      `API ${method} ${url} fallo (${status || "sin respuesta"}): ${
        error.message
      }`
    );
    reported.stack = error.stack;

    captureException(reported, {
      url,
      method,
      status: status || "network_error",
      response: error.response?.data,
    });
  }

  return Promise.reject(error);
};

axios.defaults.baseURL = process.env.VUE_APP_BASE_URL;
axios.interceptors.response.use((response) => response, responseInterceptor);

export { axios, getToken, setToken, clearToken };
